/**
 * Sophia Platform (Cloudflare Edge-Native Edition)
 *
 * @file        Multi-Model Image Studio Gallery Grid Component (Task 10.4.1)
 * @author      Le Viet Quang <quangle@fourelementscorp.com>
 * @copyright   (c) 2026 Le Viet Quang / Four Elements Corp
 * @license     MIT
 */

import React from 'react';
import { useImageStudioStore } from '../../stores/useImageStudioStore';
import { useI18nStore } from '../../stores/useI18nStore';
import {
  Heart,
  Maximize2,
  Copy,
  Download,
  Trash2,
  Sparkles,
  Clock,
  Layers,
  Zap,
} from 'lucide-react';

export const ImageGalleryGrid: React.FC = () => {
  const { t } = useI18nStore();
  const {
    historyImages,
    filterFavoriteOnly,
    openLightbox,
    toggleFavoriteImage,
    deleteImage,
    setFilterFavoriteOnly,
  } = useImageStudioStore();

  const displayedImages = filterFavoriteOnly
    ? historyImages.filter((img) => img.isFavorite)
    : historyImages;

  const handleCopyPrompt = (e: React.MouseEvent, prompt: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(prompt);
  };

  const handleDownload = (e: React.MouseEvent, url: string, id: string) => {
    e.stopPropagation();
    const link = document.createElement('a');
    link.href = url;
    link.download = `sophia-art-${id}.jpg`;
    link.target = '_blank';
    link.rel = 'noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div className="space-y-4">
      {/* 1. Header Toolbar */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <h3 className="font-heading font-bold text-base text-foreground flex items-center gap-2">
            <Layers className="w-4 h-4 text-primary" />
            <span>{t('media.galleryTitle')}</span>
          </h3>
          <span className="text-xs font-mono text-foreground-muted px-2 py-0.5 rounded-full bg-surface-elevated border border-surface-border">
            {historyImages.length}
          </span>
        </div>

        {/* Filter Favorites Toggle */}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setFilterFavoriteOnly(!filterFavoriteOnly)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl border text-xs font-semibold transition-all ${
              filterFavoriteOnly
                ? 'border-rose-500/40 bg-rose-500/10 text-rose-500 ring-1 ring-rose-500/40'
                : 'border-surface-border bg-surface hover:bg-surface-elevated text-foreground-muted'
            }`}
          >
            <Heart className={`w-3.5 h-3.5 ${filterFavoriteOnly ? 'fill-rose-500' : ''}`} />
            <span>{t('templates.favorites')}</span>
          </button>
        </div>
      </div>

      {/* 2. Gallery Grid */}
      {displayedImages.length === 0 ? (
        <div className="p-12 text-center rounded-2xl border border-dashed border-surface-border bg-surface-elevated/30 space-y-3">
          <div className="w-12 h-12 rounded-2xl bg-primary/10 text-primary flex items-center justify-center mx-auto">
            <Sparkles className="w-6 h-6" />
          </div>
          <div className="space-y-1">
            <h4 className="font-heading font-bold text-sm text-foreground">
              {filterFavoriteOnly ? t('templates.noFavorites') : t('media.emptyGallery')}
            </h4>
            <p className="text-xs text-foreground-muted max-w-sm mx-auto">
              {filterFavoriteOnly
                ? t('templates.noFavoritesDesc')
                : t('media.gallerySubtitle')}
            </p>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
          {displayedImages.map((image) => (
            <div
              key={image.id}
              onClick={() => openLightbox(image)}
              className="group rounded-2xl border border-surface-border bg-surface overflow-hidden shadow-sm hover:shadow-glow-primary/20 hover:border-primary/50 transition-all duration-300 cursor-pointer flex flex-col justify-between"
            >
              {/* Image Thumbnail Container */}
              <div className="relative aspect-square overflow-hidden bg-surface-elevated">
                <img
                  src={image.url}
                  alt={image.prompt}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />

                {/* Favorite Badge Top Right */}
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleFavoriteImage(image.id);
                  }}
                  className={`absolute top-2.5 right-2.5 w-8 h-8 rounded-full backdrop-blur-md flex items-center justify-center transition-all ${
                    image.isFavorite
                      ? 'bg-rose-500 text-white shadow-lg'
                      : 'bg-black/40 text-white/80 hover:text-white hover:bg-black/60'
                  }`}
                  title={image.isFavorite ? t('templates.removeFromFavorites') : t('templates.addToFavorites')}
                >
                  <Heart className={`w-4 h-4 ${image.isFavorite ? 'fill-white' : ''}`} />
                </button>

                {/* Model Badge Top Left */}
                <div className="absolute top-2.5 left-2.5 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-md border border-white/10 text-[10px] font-mono text-white flex items-center gap-1">
                  <Zap className="w-2.5 h-2.5 text-accent" />
                  <span>{image.modelName}</span>
                </div>

                {/* Hover Action Overlay Bottom */}
                <div className="absolute inset-x-0 bottom-0 p-2.5 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex items-center justify-between opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                  <div className="flex items-center gap-1">
                    <button
                      type="button"
                      onClick={(e) => handleCopyPrompt(e, image.prompt)}
                      className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                      title={t('common.copy')}
                    >
                      <Copy className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => handleDownload(e, image.url, image.id)}
                      className="p-1.5 rounded-lg bg-white/20 hover:bg-white/30 text-white transition-colors"
                      title={t('media.downloadImage')}
                    >
                      <Download className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteImage(image.id);
                      }}
                      className="p-1.5 rounded-lg bg-rose-500/30 hover:bg-rose-500/50 text-rose-200 transition-colors"
                      title={t('media.deleteImage')}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>

                  <span className="flex items-center gap-1 text-[11px] text-white/90 font-medium">
                    <Maximize2 className="w-3.5 h-3.5" />
                    <span>{t('media.viewFull')}</span>
                  </span>
                </div>
              </div>

              {/* Card Meta Footer */}
              <div className="p-3 space-y-1.5 border-t border-surface-border">
                <p className="text-xs text-foreground font-medium line-clamp-2 leading-relaxed">
                  {image.prompt}
                </p>
                <div className="flex items-center justify-between text-[10px] text-foreground-muted font-mono pt-1">
                  <span>{image.aspectRatio} • {image.width}x{image.height}</span>
                  <span className="flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    <span>{t('common.preview')}</span>
                  </span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
